import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { SectionHeader } from '@/components/SectionHeader';
import { Button } from '@/components/ui/button';
import { Target, RefreshCw, AlertCircle, Mail, Building2, Calendar, Star, BarChart3 } from 'lucide-react';
import {
  OverallSummaryCard,
  ScorecardBreakdown,
  PeriodSelector,
  StatusBadge,
  StatusDonut,
} from '@/components/scorecard/ScorecardPrimitives';
import CreativeMyScorecardView from '@/components/scorecard/CreativeMyScorecardView';
import PerformanceInsightsView from '@/components/scorecard/PerformanceInsightsView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyScorecardPage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoSwitched, setAutoSwitched] = useState(false);
  const [appliedDefault, setAppliedDefault] = useState(false);

  // Creative team membership detection
  const [isCreativeTeamMember, setIsCreativeTeamMember] = useState(false);

  // Persist user's default channel preference (per browser/device)
  const DEFAULT_CHANNEL_KEY = 'my_scorecard_default_channel';
  const [selectedChannel, setSelectedChannel] = useState(() => {
    try { return localStorage.getItem(DEFAULT_CHANNEL_KEY) || 'all'; }
    catch { return 'all'; }
  });

  const fetchScorecard = async (p = period) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/scorecard/my-scorecard?period=${p}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(r.data);
      // On first load, if current period has 0 metrics + there's a latest available period, auto-switch once
      if (!autoSwitched && r.data?.success && (r.data.breakdown?.length || 0) === 0) {
        const available = r.data.available_periods || [];
        if (available.length > 0 && available[0] !== p) {
          setAutoSwitched(true);
          setPeriod(available[0]);
          return;
        }
      }
    } catch (e) {
      toast.error('Gagal load scorecard: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchScorecard(period); }, [period]);

  // Detect creative team membership once on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API}/creative-scorecard/team-members`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        try {
          const me = JSON.parse(localStorage.getItem('user') || '{}');
          const memberIds = r.data?.member_ids || [];
          if (me?.id && memberIds.includes(me.id)) {
            setIsCreativeTeamMember(true);
          }
        } catch { /* ignore */ }
      })
      .catch(() => { /* not fatal */ });
  }, []);

  // Apply default channel preference once data loads
  useEffect(() => {
    if (appliedDefault || !data?.success) return;
    try {
      const saved = localStorage.getItem(DEFAULT_CHANNEL_KEY);
      const validIds = ['all', ...(data.channels || []).map(c => c.staff_node_id)];
      if (saved && validIds.includes(saved)) {
        setSelectedChannel(saved);
      } else if (saved && !validIds.includes(saved)) {
        // Stale default — clear it
        localStorage.removeItem(DEFAULT_CHANNEL_KEY);
        setSelectedChannel('all');
      }
      setAppliedDefault(true);
    } catch {
      setAppliedDefault(true);
    }
  }, [data, appliedDefault]);

  const handleSetDefault = (channelId) => {
    try {
      const current = localStorage.getItem(DEFAULT_CHANNEL_KEY);
      if (current === channelId) {
        localStorage.removeItem(DEFAULT_CHANNEL_KEY);
        toast.success('Default channel cleared — will show All Channels next visit');
      } else {
        localStorage.setItem(DEFAULT_CHANNEL_KEY, channelId);
        const channelName = channelId === 'all'
          ? 'All Channels'
          : (data?.channels?.find(c => c.staff_node_id === channelId)?.channel_name || 'Selected');
        toast.success(`"${channelName}" set as default`);
      }
    } catch (e) { /* localStorage unavailable */ }
  };

  const defaultChannelId = (() => {
    try { return localStorage.getItem(DEFAULT_CHANNEL_KEY); } catch { return null; }
  })();

  // Tab: current-month scorecard vs multi-month insights
  const [activeTab, setActiveTab] = useState('current'); // 'current' | 'insights'

  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] p-4 lg:p-6" data-testid="my-scorecard-page">
        <SectionHeader
          icon={Target}
          title="My Scorecard"
          subtitle="Performance personal awak — track gap to target"
          iconBg="bg-gradient-to-br from-[#10B981] to-[#3B82F6] shadow-[#10B981]/20"
          action={
            <div className="flex items-center gap-2">
              <PeriodSelector value={period} onChange={setPeriod} available={data?.available_periods || []} />
              <Button onClick={fetchScorecard} disabled={loading} size="sm" variant="outline" data-testid="refresh-scorecard-btn"
                className="h-9 border-[hsl(var(--border))] hover:border-[hsl(var(--border-hover))]">
                <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />Refresh
              </Button>
            </div>
          }
        />

        {/* View toggle removed — creative team members ONLY see Creative View; others see Standard. */}

        {/* Segmented tab: Current Month vs Insights (6/12-month) */}
        <div className="mt-5 inline-flex p-1 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))]" data-testid="scorecard-tabs">
          <button
            onClick={() => setActiveTab('current')}
            className={`px-4 py-2 text-xs font-bold rounded-md transition-colors flex items-center gap-1.5 ${activeTab === 'current' ? 'bg-[#10B981]/20 text-[#10B981]' : 'text-[hsl(var(--text-muted))] hover:text-white'}`}
            data-testid="tab-current-month"
          >
            <Target size={12} />
            Current Month
          </button>
          <button
            onClick={() => setActiveTab('insights')}
            className={`px-4 py-2 text-xs font-bold rounded-md transition-colors flex items-center gap-1.5 ${activeTab === 'insights' ? 'bg-[#3B82F6]/20 text-[#3B82F6]' : 'text-[hsl(var(--text-muted))] hover:text-white'}`}
            data-testid="tab-insights"
          >
            <BarChart3 size={12} />
            Insights
          </button>
        </div>

        {/* Insights tab content — multi-month achievement % + alerts history */}
        {activeTab === 'insights' && (
          <div className="mt-5 max-w-7xl">
            <PerformanceInsightsView months={6} />
          </div>
        )}

        {/* Creative view (only shown to creative team members when on Current tab) */}
        {activeTab === 'current' && isCreativeTeamMember && (
          <div className="mt-5 max-w-7xl">
            <CreativeMyScorecardView period={period} />
          </div>
        )}

        {/* Standard view (everyone else on Current tab) */}
        {activeTab === 'current' && !isCreativeTeamMember && (

        <div className="mt-5 space-y-5 max-w-7xl">
          {loading && !data && (
            <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-8 text-center">
              <RefreshCw size={20} className="animate-spin mx-auto text-[#6B7280] mb-2" />
              <p className="text-sm text-[#9CA3AF]">Loading scorecard...</p>
            </div>
          )}

          {data?.error === 'no_snapshot' && (
            <div className="bg-[#F59E0B]/5 border border-[#F59E0B]/20 rounded-xl p-6" data-testid="no-snapshot-warning">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-[#F59E0B] flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <h3 className="text-white font-semibold mb-1">Scorecard belum di-sync</h3>
                  <p className="text-sm text-[#9CA3AF]">Admin perlu sync data dari external Scorecard API dulu. Hubungi admin awak.</p>
                </div>
              </div>
            </div>
          )}

          {data?.error === 'no_mapping' && (
            <div className="bg-[#3B82F6]/5 border border-[#3B82F6]/20 rounded-xl p-6" data-testid="no-mapping-warning">
              <div className="flex items-start gap-3">
                <Mail className="text-[#3B82F6] flex-shrink-0 mt-0.5" size={20} />
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-1">Awak belum di-link ke Scorecard</h3>
                  <p className="text-sm text-[#9CA3AF] mb-3">
                    Akaun CRM awak belum di-pair dengan STAFF node dalam Scorecard system. Selepas admin link, scorecard awak akan muncul di sini.
                  </p>
                  <p className="text-xs text-[#6B7280]">
                    Hubungi admin → Admin Panel → Scorecard → User Mappings → pilih awak punya STAFF node.
                  </p>
                </div>
              </div>
            </div>
          )}

          {data?.success && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-1">
                  <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-5 h-full">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-[#10B981]/15 flex items-center justify-center">
                        <Target className="text-[#10B981]" size={14} />
                      </div>
                      <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Scorecard Identity</p>
                    </div>
                    <p className="text-white text-lg font-semibold leading-tight">{data.staff?.name}</p>
                    {data.staff?.description && (
                      <p className="text-xs text-[#6B7280] mt-2">{data.staff.description}</p>
                    )}
                    <div className="mt-4 pt-4 border-t border-[hsl(var(--border))]">
                      <div className="flex items-center gap-1.5 text-[10px] text-[#6B7280]">
                        <Calendar size={11} />Snapshot last synced
                      </div>
                      <p className="text-xs text-[#9CA3AF] mt-0.5">
                        {data.snapshot_fetched_at ? new Date(data.snapshot_fetched_at).toLocaleString('en-MY', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <OverallSummaryCard overall={data.overall} label={`Overall — ${data.period}`} />
                </div>
              </div>

              <div>
                <div className="flex flex-col gap-3 mb-3">
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="text-white text-base font-semibold">KPI Breakdown</h2>
                    <span className="text-xs text-[#6B7280]">
                      {(selectedChannel === 'all'
                        ? data.breakdown?.length
                        : (data.channels?.find(c => c.staff_node_id === selectedChannel)?.breakdown?.length || 0)
                      )} metric{((selectedChannel === 'all' ? data.breakdown?.length : 0) === 1) ? '' : 's'}
                    </span>
                  </div>
                  {/* Channel tabs — drill-down per channel */}
                  {data.channels && data.channels.length >= 1 && (
                    <div className="flex items-center gap-1.5 flex-wrap bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg p-1">
                      <div className={`flex items-center rounded-md transition ${
                        selectedChannel === 'all'
                          ? 'bg-gradient-to-r from-[#10B981] to-[#3B82F6] shadow-[0_2px_8px_rgba(16,185,129,0.3)]'
                          : 'hover:bg-[hsl(var(--surface-elevated))]'
                      }`}>
                        <button
                          onClick={() => setSelectedChannel('all')}
                          data-testid="my-scorecard-tab-all"
                          className={`px-3 py-1.5 text-xs font-semibold transition flex items-center gap-1.5 ${
                            selectedChannel === 'all' ? 'text-white' : 'text-[#9CA3AF] hover:text-white'
                          }`}>
                          <Target size={11} />
                          All Channels
                          <span className="text-[9px] opacity-70">({data.channels.length})</span>
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleSetDefault('all'); }}
                          data-testid="my-scorecard-default-all"
                          title={defaultChannelId === 'all' ? 'Default — click to clear' : 'Set as default'}
                          className={`pr-2 pl-0.5 py-1.5 transition ${
                            defaultChannelId === 'all' ? 'text-[#F59E0B]' : selectedChannel === 'all' ? 'text-white/60 hover:text-white' : 'text-[#6B7280] hover:text-[#F59E0B]'
                          }`}>
                          <Star size={11} fill={defaultChannelId === 'all' ? 'currentColor' : 'none'} />
                        </button>
                      </div>
                      {data.channels.map(ch => {
                        const isActive = selectedChannel === ch.staff_node_id;
                        const isDefault = defaultChannelId === ch.staff_node_id;
                        const statusMap = { on_track: '#10B981', at_risk: '#F59E0B', off_track: '#E63946', no_data: '#6B7280' };
                        const dotColor = statusMap[ch.overall?.status] || '#6B7280';
                        return (
                          <div key={ch.staff_node_id} className={`flex items-center rounded-md transition ${
                            isActive ? 'bg-gradient-to-r from-[#10B981] to-[#3B82F6] shadow-[0_2px_8px_rgba(16,185,129,0.3)]' : 'hover:bg-[hsl(var(--surface-elevated))]'
                          }`}>
                            <button
                              onClick={() => setSelectedChannel(ch.staff_node_id)}
                              data-testid={`my-scorecard-tab-${ch.staff_node_id}`}
                              className={`px-3 py-1.5 text-xs font-semibold transition flex items-center gap-1.5 ${
                                isActive ? 'text-white' : 'text-[#9CA3AF] hover:text-white'
                              }`}>
                              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: isActive ? '#fff' : dotColor }} />
                              {ch.channel_name || ch.ib_name}
                              <span className="text-[9px] opacity-70 tabular-nums">{Math.min(ch.overall?.overall_pct || 0, 100).toFixed(0)}%</span>
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleSetDefault(ch.staff_node_id); }}
                              data-testid={`my-scorecard-default-${ch.staff_node_id}`}
                              title={isDefault ? 'Default — click to clear' : 'Set as default'}
                              className={`pr-2 pl-0.5 py-1.5 transition ${
                                isDefault ? 'text-[#F59E0B]' : isActive ? 'text-white/60 hover:text-white' : 'text-[#6B7280] hover:text-[#F59E0B]'
                              }`}>
                              <Star size={11} fill={isDefault ? 'currentColor' : 'none'} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <ScorecardBreakdown
                  breakdown={
                    selectedChannel === 'all'
                      ? (data.breakdown || [])
                      : (data.channels?.find(c => c.staff_node_id === selectedChannel)?.breakdown || [])
                  }
                />
              </div>

              {data.channels && data.channels.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 size={16} className="text-[#9CA3AF]" />
                    <h2 className="text-white text-base font-semibold">By Channel</h2>
                    <span className="text-xs text-[#6B7280]">({data.channels.length})</span>
                    <span className="text-[10px] text-[#6B7280] ml-auto italic">Klik kad untuk drill-down</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {data.channels.map((ch) => {
                      const meta = ch.overall?.status || 'no_data';
                      const top3 = (ch.breakdown || []).filter(t => t.status === 'on_track').slice(0, 3);
                      const struggling = (ch.breakdown || []).filter(t => t.status === 'off_track').slice(0, 2);
                      const accentMap = { on_track: '#10B981', at_risk: '#F59E0B', off_track: '#E63946', no_data: '#6B7280' };
                      const accent = accentMap[meta];
                      const avatarUrl = ch.brand_logo_url || ch.personal_image_url;
                      const initials = (ch.channel_name || ch.ib_name || '?')
                        .split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
                      const isActiveTab = selectedChannel === ch.staff_node_id;
                      return (
                      <div key={ch.staff_node_id || ch.ib_node_id}
                        onClick={() => setSelectedChannel(ch.staff_node_id)}
                        className={`group relative bg-[hsl(var(--surface))] border rounded-2xl overflow-hidden hover:-translate-y-0.5 transition-all cursor-pointer ${
                          isActiveTab ? 'border-[#10B981] shadow-[0_0_0_2px_rgba(16,185,129,0.15)]' : 'border-[hsl(var(--border))] hover:border-[hsl(var(--border-hover))]'
                        }`}
                        data-testid={`channel-tile-${ch.staff_node_id || ch.ib_node_id}`}>
                        <div className="absolute inset-x-0 top-0 h-1" style={{ background: `linear-gradient(90deg, ${accent}, ${accent}66, transparent)` }} />
                        <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full blur-3xl opacity-15 group-hover:opacity-30 transition-opacity" style={{ backgroundColor: accent }} />
                        <div className="relative p-4">
                          <div className="flex items-start gap-3 mb-3">
                            <div className="relative w-11 h-11 rounded-xl overflow-hidden flex-shrink-0 ring-1 ring-[hsl(var(--border))]" style={{ background: `linear-gradient(135deg, ${accent}22, ${accent}11)` }}>
                              {avatarUrl ? (
                                <img src={avatarUrl} alt={ch.channel_name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-white font-bold text-xs" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}80)` }}>
                                  {initials}
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[9px] text-[#6B7280] uppercase tracking-wider">Channel</p>
                              <p className="text-white font-semibold text-sm truncate">{ch.channel_name || ch.ib_name}</p>
                              <p className="text-[10px] text-[#6B7280] truncate">{ch.staff_name}</p>
                            </div>
                            <StatusBadge status={meta} />
                          </div>
                          <div className="flex items-end justify-between mb-2 gap-2">
                            <div>
                              <p className="text-[9px] text-[#6B7280] uppercase tracking-wider">Overall</p>
                              <p className="text-3xl font-bold text-white tabular-nums tracking-tight">{Math.min(ch.overall?.overall_pct || 0, 100).toFixed(1)}<span className="text-sm text-[#6B7280]">%</span></p>
                            </div>
                            <StatusDonut overall={ch.overall} size={48} />
                          </div>
                          {top3.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-[hsl(var(--border))]">
                              <p className="text-[9px] text-[#10B981] uppercase tracking-wider mb-1">Top performing</p>
                              <div className="space-y-0.5">
                                {top3.map(t => (
                                  <div key={t.template_id} className="flex items-center gap-2 text-[10px]">
                                    <span className="text-[#9CA3AF] flex-1 truncate">{t.name}</span>
                                    <span className="text-[#10B981] font-semibold">{Math.min(t.achievement_pct, 100).toFixed(0)}%</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {struggling.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-[hsl(var(--border))]">
                              <p className="text-[9px] text-[#E63946] uppercase tracking-wider mb-1">Needs attention</p>
                              <div className="space-y-0.5">
                                {struggling.map(t => (
                                  <div key={t.template_id} className="flex items-center gap-2 text-[10px]">
                                    <span className="text-[#9CA3AF] flex-1 truncate">{t.name}</span>
                                    <span className="text-[#E63946] font-semibold">{Math.min(t.achievement_pct, 100).toFixed(0)}%</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {isActiveTab && (
                            <div className="mt-3 pt-2 border-t border-[#10B981]/30 flex items-center gap-1.5 text-[10px] text-[#10B981] font-semibold">
                              <Target size={10} />
                              KPI Breakdown drilled into this channel ↑
                            </div>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        )}
      </div>
    </ResponsiveLayout>
  );
}
